import React from 'react';
import { Box, Grid } from '@drivekyte/web-components';
import { Box as BoxV2, Typography, useMedia, Button } from '@drivekyte/ui';
import Image from 'next/image';
import styled, { css } from 'styled-components';
import WPTypography from '@/components/typography';
import SplitParagraph from '../split-paragraph';

export type TripItemProps = {
  index: number;
  heading: string;
  distance: string;
  image: { src: string; alt: string };
  body: string;
  labels: { travel: string; recommend: string; cta: string };
  car: string;
  handleSearch: () => void;
};

const TripItem = ({
  index,
  heading,
  distance,
  image,
  body,
  car,
  labels,
  handleSearch,
}: TripItemProps) => {
  const media = useMedia();
  const isTablet = media.sm;

  return (
    <Box marginBottom="small">
      <Grid
        container
        spacing={4}
        direction={
          isTablet ? 'column-reverse' : index % 2 ? 'row' : 'row-reverse'
        }
      >
        <Grid item md={6} sm={12}>
          <Box position="relative" height="100%">
            <ResponsiveSquare>
              <BoxV2 padding="$medium" $gtLg={{ padding: '$xLarge' }}>
                <Box marginBottom="small">
                  <Typography variant="headingMedium">
                    {`${index}. ${heading}`}
                  </Typography>
                </Box>
                <Box marginBottom="small">
                  <WPTypography
                    variant="p"
                    fontSize="17"
                    fontWeight={800}
                    color="primary"
                  >
                    {`${labels.travel}: ${distance}`}
                  </WPTypography>
                </Box>
                <SplitParagraph>{body}</SplitParagraph>
              </BoxV2>
              <Box
                padding="xLarge"
                width={isTablet ? 'auto' : '100%'}
                position={isTablet ? 'relative' : 'absolute'}
                bottom={0}
                left={0}
              >
                <Grid container justifyContent="space-around">
                  <Grid
                    item
                    sm={6}
                    justifyContent={'center'}
                    alignItems={'center'}
                    display={'flex'}
                    flexDirection={'column'}
                  >
                    <Typography
                      variant="paragraphSmall"
                      color="$backgroundPositive"
                    >
                      {labels.recommend}
                    </Typography>
                    <Typography
                      variant="paragraphMedium"
                      color="$backgroundPositive"
                      fontWeight={'800'}
                    >
                      {car}
                    </Typography>
                  </Grid>
                  <Grid item sm={6}>
                    <Button
                      onPress={handleSearch}
                      maxWidth="50%"
                      variant="primary"
                    >
                      {labels.cta}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </ResponsiveSquare>
          </Box>
        </Grid>
        <Grid item md={6} sm={12}>
          <Box
            overflow="hidden"
            borderRadius="large"
            height="100%"
            minHeight="300px"
            position="relative"
          >
            <ResponsiveSquare>
              <Image
                src={image.src}
                alt={image.alt}
                layout="fill"
                objectFit="cover"
              />
            </ResponsiveSquare>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

const ResponsiveSquare = styled.div`
  ${({ theme }) => css`
    @media (min-width: ${theme.breakpoints.md}) {
      padding-bottom: 105%;
      position: relative;
      top: 0;
      left: 0;
      height: 0;
    }
    @media (min-width: ${theme.breakpoints.lg}) {
      padding-bottom: 86.25%;
    }
    background: rgba(9, 138, 34, 0.05);
    border-radius: ${theme.borderRadius.large}px;
    overflow: hidden;
  `}
`;

export default TripItem;
